import React, { useState, useRef, useEffect } from 'react';
import { Timestamp } from 'firebase/firestore';
import { BottomSheet } from './ui/BottomSheet';
import { createProject } from '../services/projectService';
import { useNotifications } from '../contexts/NotificationContext';
import { useOnlineStatus } from '../hooks/useOnlineStatus';

interface QuickAddProjectSheetProps {
  isOpen: boolean;
  onClose: () => void;
  /** Called with the new project id once it is created. */
  onCreated?: (projectId: string) => void;
}

/** Local midnight as a Timestamp, so a date carries no misleading time. */
function dateOnly(d: Date): Timestamp {
  const copy = new Date(d);
  copy.setHours(0, 0, 0, 0);
  return Timestamp.fromDate(copy);
}

/**
 * Quick capture for a new home ("Ny bostad").
 *
 * Why this exists: the full project form is 15 fields across 7 sections, so a
 * valuator standing in a doorway had a faster option — typing the address
 * straight into Google Calendar — and took it. That put homes outside the app
 * entirely. This sheet is deliberately cheaper than the alternative: one
 * address line, an optional date, and done.
 *
 * Only the address is required here. The full form's other three "required"
 * fields (postal town, contact name, phone) are frequently unknown at the point
 * of first contact, and demanding them is what pushed people out. Everything
 * else is filled in later, as the project matures toward a pickup.
 */
export function QuickAddProjectSheet({ isOpen, onClose, onCreated }: QuickAddProjectSheetProps) {
  const { addNotification } = useNotifications();
  const isOnline = useOnlineStatus();
  const inputRef = useRef<HTMLInputElement>(null);

  const [address, setAddress] = useState('');
  const [city, setCity] = useState('');
  const [contactName, setContactName] = useState('');
  const [contactPhone, setContactPhone] = useState('');
  const [visitDate, setVisitDate] = useState<Date | null>(null);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Reset whenever the sheet opens so a previous entry never leaks into the next.
  useEffect(() => {
    if (isOpen) {
      setAddress(''); setCity(''); setContactName(''); setContactPhone('');
      setVisitDate(null); setError(null); setSaving(false);
    }
  }, [isOpen]);

  const today = new Date();
  const tomorrow = new Date(today);
  tomorrow.setDate(tomorrow.getDate() + 1);

  const isSameDate = (a: Date | null, b: Date) =>
    !!a && a.toDateString() === b.toDateString();

  const handleSave = async () => {
    if (!address.trim()) {
      setError('Skriv in en adress för att spara bostaden.');
      inputRef.current?.focus();
      return;
    }
    if (!isOnline) {
      setError('Du är offline. Bostaden kan sparas när du har anslutning igen.');
      return;
    }

    try {
      setSaving(true);
      setError(null);
      const { id } = await createProject({
        address: {
          street: address.trim(),
          city: city.trim(),
          postalCode: '',
          coordinates: null,
        },
        contact: {
          name: contactName.trim(),
          phone: contactPhone.trim(),
        },
        dates: {
          evaluation: visitDate ? dateOnly(visitDate) : null,
          scheduledPickup: null,
          actualPickup: null,
        },
        // A quick-added home starts at the beginning of the lifecycle: recorded,
        // not yet valued. It matures to "värderad" once the valuation is done.
        status: 'bostad',
        generalNotes: '',
        evaluator: '',
      } as any);

      addNotification({
        type: 'success',
        message: visitDate
          ? 'Bostaden sparad och inlagd i kalendern.'
          : 'Bostaden sparad.',
        duration: 2500,
      });
      onCreated?.(id);
      onClose();
    } catch (err) {
      console.error('Quick add failed:', err);
      setError('Kunde inte spara bostaden. Kontrollera anslutningen och försök igen.');
    } finally {
      setSaving(false);
    }
  };

  const fieldWrap = 'bg-[var(--bg-surface-alt)] rounded-ios-input px-3.5 min-h-[44px] flex items-center';
  const fieldInput =
    'bg-transparent text-[15px] text-[var(--text-primary)] w-full placeholder-[var(--text-tertiary)] outline-none';

  const dateChip = (label: string, active: boolean, onClick: () => void) => (
    <button
      type="button"
      onClick={onClick}
      aria-pressed={active}
      className={`min-h-[44px] px-4 rounded-full text-[15px] font-medium transition-colors ${
        active
          ? 'bg-accent-600 text-white'
          : 'bg-[var(--bg-surface-alt)] text-[var(--text-secondary)] active:bg-[var(--bg-surface-hover)]'
      }`}
    >
      {label}
    </button>
  );

  return (
    <BottomSheet isOpen={isOpen} onClose={onClose} title="Ny bostad">
      <div className="px-5 pb-5 space-y-4">
        <p className="text-[13px] text-[var(--text-secondary)] -mt-1">
          Räcker med adressen nu. Resten fyller du i på plats.
        </p>

        <div className="space-y-2">
          <label htmlFor="qa-address" className="block text-[13px] font-medium text-[var(--text-secondary)]">
            Adress
          </label>
          <div className={fieldWrap}>
            <input
              id="qa-address"
              ref={inputRef}
              type="text"
              value={address}
              onChange={(e) => { setAddress(e.target.value); if (error) setError(null); }}
              placeholder="Storgatan 12"
              autoComplete="street-address"
              className={fieldInput}
            />
          </div>
          <div className={fieldWrap}>
            <input
              type="text"
              value={city}
              onChange={(e) => setCity(e.target.value)}
              placeholder="Ort (valfritt)"
              autoComplete="address-level2"
              className={fieldInput}
            />
          </div>
        </div>

        <div className="space-y-2">
          <span className="block text-[13px] font-medium text-[var(--text-secondary)]">
            När ska du dit? <span className="font-normal text-[var(--text-tertiary)]">Valfritt</span>
          </span>
          <div className="flex flex-wrap gap-2">
            {dateChip('Idag', isSameDate(visitDate, today), () =>
              setVisitDate(isSameDate(visitDate, today) ? null : today))}
            {dateChip('Imorgon', isSameDate(visitDate, tomorrow), () =>
              setVisitDate(isSameDate(visitDate, tomorrow) ? null : tomorrow))}
            <div className={`${fieldWrap} flex-1 min-w-[150px]`}>
              <input
                type="date"
                aria-label="Välj annat datum"
                value={visitDate ? visitDate.toISOString().slice(0, 10) : ''}
                onChange={(e) =>
                  setVisitDate(e.target.value ? new Date(`${e.target.value}T00:00:00`) : null)
                }
                className={fieldInput}
              />
            </div>
          </div>
        </div>

        <div className="space-y-2">
          <span className="block text-[13px] font-medium text-[var(--text-secondary)]">
            Kontakt <span className="font-normal text-[var(--text-tertiary)]">Valfritt</span>
          </span>
          <div className={fieldWrap}>
            <input
              type="text"
              value={contactName}
              onChange={(e) => setContactName(e.target.value)}
              placeholder="Namn"
              autoComplete="name"
              className={fieldInput}
            />
          </div>
          <div className={fieldWrap}>
            <input
              type="tel"
              value={contactPhone}
              onChange={(e) => setContactPhone(e.target.value)}
              placeholder="Telefon"
              autoComplete="tel"
              className={fieldInput}
            />
          </div>
        </div>

        {error && (
          <p role="alert" className="text-[13px] text-red-600 dark:text-red-400">
            {error}
          </p>
        )}

        <button
          type="button"
          onClick={handleSave}
          disabled={saving}
          className="w-full min-h-[50px] rounded-xl bg-accent-600 text-white text-[17px] font-semibold active:bg-accent-700 transition-colors disabled:opacity-50"
        >
          {saving ? 'Sparar...' : 'Spara bostad'}
        </button>
      </div>
    </BottomSheet>
  );
}

export default QuickAddProjectSheet;
